/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Component, ComponentChild, RenderableProps } from 'preact';

/**
 * Supported transitions
 */
type TransitionCompleteStatus = 'entered' | 'exited';
type TransitionIntermediateStatus = 'entering' | 'exiting' | null;
type TransitionStatus = TransitionCompleteStatus | TransitionIntermediateStatus;

/**
 * A function wrapper that when cancelled, will not call the
 * original function when invoked.
 */
type CancellableFunction = {
  (): void;
  cancel?: () => void;
};

/**
 * State for the Transition component
 */
type State = {
  status: TransitionStatus;
};

/**
 * Props for the Transition Component
 */
export type TransitionProps<M = any> = {
  /**
   * A boolean for whether or not to the show the child content. Usually, this represents
   * whether or the current item existing in the data.
   */
  in?: boolean;

  /**
   * An optional metadata that will be passed along on all callbacks
   */
  metadata?: M;

  /**
   * Called when the component enters for the first time.
   */
  onEnter?: (node?: Element, metadata?: M) => void;

  /**
   * Called when the component enters for the first time and right after onEnter call.
   */
  onEntering?: (node?: Element, callback?: () => void, metadata?: M) => void;

  /**
   * Called when the component enters for the first time and all the enter transitions are over.
   */
  onEntered?: (node?: Element, metadata?: M) => void;

  /**
   * Called when the component exits.
   */
  onExit?: (node?: Element, metadata?: M) => void;

  /**
   * Called when the component exits and right after the onExit call.
   */
  onExiting?: (node?: Element, callback?: () => void, metadata?: M) => void;

  /**
   * Called when the component exits and all the transitions are over.
   */
  onExited?: (node?: Element, metadata?: M) => void;
};

/**
 * @classdesc
 * The component that acts as a layer for handing transitions.
 *
 * @ignore
 */
export class Transition<M = any> extends Component<TransitionProps<M>, State> {
  private readonly _appearStatus: TransitionIntermediateStatus;
  private _nextCallback: CancellableFunction | null;

  ////////////////////////////////////////////////////////////////////////
  // Handler functions are created as members to have them 'this' bound //
  ////////////////////////////////////////////////////////////////////////

  ///////////////////////////
  // Handler functions end //
  ///////////////////////////

  /**
   * Instantiates Component
   *
   * @param props The component properties
   */
  constructor(props: Readonly<TransitionProps<M>>) {
    super(props);

    let appearStatus: TransitionIntermediateStatus;
    if (props.in) {
      appearStatus = 'entering';
    } else {
      appearStatus = null;
    }

    this._appearStatus = appearStatus;
    this.state = { status: 'exited' };
    this._nextCallback = null;
  }

  //////////////////////////////////////
  // Component Life Cycle Hooks Start //
  //////////////////////////////////////

  /**
   * Lifecycle hook that gets called when the component is mounted to the DOM
   */
  componentDidMount() {
    this._updateStatus(this._appearStatus);
  }

  /**
   * Lifecycle hook that gets called after each update to the component
   *
   * @param prevProps The props of the component before last update
   */
  componentDidUpdate(prevProps: Readonly<TransitionProps<M>>) {
    let nextStatus: TransitionIntermediateStatus = null;

    if (prevProps !== this.props) {
      const { status } = this.state;

      if (this.props.in) {
        if (status !== 'entering' && status !== 'entered') {
          // The component is just entering, so set the next status as Entering
          nextStatus = 'entering';
        }
      } else {
        if (status === 'entering' || status === 'entered') {
          // The component is not in the data anymore, so we need to do exit animation
          // So, set the next status as Exiting
          nextStatus = 'exiting';
        }
      }
    }

    this._updateStatus(nextStatus);
  }

  /**
   * Lifecycle hook that gets called right before the component unmounts
   */
  componentWillUnmount() {
    this._cancelNextCallback();
  }

  ////////////////////////////////////
  // Component Life Cycle Hooks End //
  ////////////////////////////////////

  /**
   * Renders the Transition component
   *
   * @param props The current props
   * @returns The rendered component child
   */
  render(props?: RenderableProps<TransitionProps<M>>): ComponentChild {
    return props?.children;
  }

  ////////////////////////////
  // Private helper methods //
  ////////////////////////////

  /**
   * Creates a wrapper callback function, which can be cancelled.
   *
   * @param callback The current callback function
   * @returns The created cancellable callback
   */
  private _setNextCallback(callback: (...args: any) => void): CancellableFunction {
    let active = true;

    this._nextCallback = (...args: any[]) => {
      if (active) {
        active = false;
        this._nextCallback = null;

        callback(...args);
      }
    };

    this._nextCallback.cancel = () => {
      active = false;
    };

    return this._nextCallback;
  }

  /**
   * Cancels the scheduled next callback
   */
  private _cancelNextCallback() {
    this._nextCallback?.cancel?.();
    this._nextCallback = null;
  }

  /**
   * Updates the status of the component. Performs corresponding Transitions.
   */
  private _updateStatus(nextStatus: TransitionIntermediateStatus) {
    if (nextStatus != null) {
      this._cancelNextCallback();

      if (nextStatus === 'entering') {
        this._performEnter(this.base as Element); // In our component, base is always Element
      } else {
        this._performExit(this.base as Element); // In our component, base is always Element
      }
    }
  }

  /**
   * Perform Entering transitions
   *
   * @param node The root DOM element of this component
   */
  private _performEnter(node?: Element) {
    this.props.onEnter?.(node, this.props.metadata);

    this.setState({ status: 'entering' }, () => {
      this.props.onEntering?.(
        node,
        this._setNextCallback(() => {
          this.setState({ status: 'entered' }, () => {
            this.props.onEntered?.(node, this.props.metadata);
          });
        }),
        this.props.metadata
      );
    });
  }

  /**
   * Perform Exiting transitions
   *
   * @param node The root DOM element of this component
   */
  private _performExit(node?: Element) {
    this.props.onExit?.(node, this.props.metadata);

    this.setState({ status: 'exiting' }, () => {
      this.props.onExiting?.(
        node,
        this._setNextCallback(() => {
          this.setState({ status: 'exited' }, () => {
            this.props.onExited?.(node, this.props.metadata);
          });
        }),
        this.props.metadata
      );
    });
  }
}
